
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { GroupMember, GroupRole } from "@/lib/types";

interface ConfirmationDialogsProps {
  members: GroupMember[];
  confirmAction: string | null;
  setConfirmAction: (action: string | null) => void;
  memberIdToPromote: string | null;
  memberIdToDemote: string | null;
  memberIdToBan: string | null;
  memberIdToUnban: string | null;
  memberIdToMute: string | null;
  memberIdToUnmute: string | null;
  memberIdToKick: string | null;
  onPromote: (memberId: string, role: GroupRole) => void;
  onDemote: (memberId: string, userId: string, role: GroupRole) => void;
  onBan: (memberId: string, userId: string) => void;
  onUnban: (memberId: string) => void;
  onMute: (memberId: string, userId: string) => void;
  onUnmute: (memberId: string) => void;
  onKick: (memberId: string, userId: string) => void;
}

export function ConfirmationDialogs({
  members,
  confirmAction,
  setConfirmAction,
  memberIdToPromote,
  memberIdToDemote,
  memberIdToBan,
  memberIdToUnban,
  memberIdToMute,
  memberIdToUnmute,
  memberIdToKick,
  onPromote,
  onDemote,
  onBan,
  onUnban,
  onMute,
  onUnmute,
  onKick
}: ConfirmationDialogsProps) {
  const handleCloseDialog = () => setConfirmAction(null);

  return (
    <>
      <AlertDialog open={confirmAction === "promote" && !!memberIdToPromote} onOpenChange={(open) => !open && handleCloseDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to promote this member? This will give them additional permissions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDialog}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                const member = members.find(m => m.id === memberIdToPromote);
                if (member) onPromote(member.id, member.role);
              }}
            >
              Promote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAction === "demote" && !!memberIdToDemote} onOpenChange={(open) => !open && handleCloseDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Demote Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to demote this member? This will reduce their permissions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDialog}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                const member = members.find(m => m.id === memberIdToDemote);
                if (member) onDemote(member.id, member.userId, member.role);
              }}
            >
              Demote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAction === "ban" && !!memberIdToBan} onOpenChange={(open) => !open && handleCloseDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ban Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to ban this member? They will not be able to participate in the group but will still be a member.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDialog}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                const member = members.find(m => m.id === memberIdToBan);
                if (member) onBan(member.id, member.userId);
              }}
              className="bg-destructive text-destructive-foreground"
            >
              Ban
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAction === "unban" && !!memberIdToUnban} onOpenChange={(open) => !open && handleCloseDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unban Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unban this member? They will be able to participate in the group again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDialog}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                const member = members.find(m => m.id === memberIdToUnban);
                if (member) onUnban(member.id);
              }}
            >
              Unban
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAction === "mute" && !!memberIdToMute} onOpenChange={(open) => !open && handleCloseDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mute Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mute this member for 1 hour? They will not be able to send messages during this time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDialog}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                const member = members.find(m => m.id === memberIdToMute);
                if (member) onMute(member.id, member.userId);
              }}
            >
              Mute
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAction === "unmute" && !!memberIdToUnmute} onOpenChange={(open) => !open && handleCloseDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unmute Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unmute this member? They will be able to send messages again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDialog}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                const member = members.find(m => m.id === memberIdToUnmute);
                if (member) onUnmute(member.id);
              }}
            >
              Unmute
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAction === "kick" && !!memberIdToKick} onOpenChange={(open) => !open && handleCloseDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kick Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to kick this member? They will be removed from the group immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseDialog}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                const member = members.find(m => m.id === memberIdToKick);
                if (member) onKick(member.id, member.userId);
              }}
              className="bg-destructive text-destructive-foreground"
            >
              Kick
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
